import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { downloadLensCsv } from '~/utils/downloadLensCsv';

import BlockData from './BlockData';
import BlockScanEstimate from './BlockScanEstimate';
import BlockQuery from './BlockQuery';
import { controlReach } from './BlockView';
import { Heading, Pill, periodLine } from './LensAbout';
import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';
import { findBlock } from './editor';
import { PanelHeader, Section } from './settingsKit';
import { renderedBlockTitle } from './titles';
import type { Envelope, LensDocument, LensDocumentData, LensState } from './types';

export type BlockPaneView = 'about' | 'sql' | 'data';

export type InspectedBlock = { id: string; title: string; view: BlockPaneView; asked?: number };

type BlockPaneProps = {
  block: InspectedBlock;
  lensId: string;
  lensVersion: number;
  state: LensState;
  data?: LensDocumentData;
  about?: Array<string>;
  onView: (view: BlockPaneView) => void;
  onClose: () => void;
  onEdit?: () => void;
  embedded?: boolean;
  document?: LensDocument;
};

export default function BlockPane({ block, lensId, lensVersion, state, data, about = [], onView, onClose, onEdit, embedded = false, document }: BlockPaneProps) {
  const fields = useGetReportingLensFields(lensId, lensVersion);
  const { enqueueSnackbar } = useSnackbar();
  const planned = data?.blocks[block.id];
  const spec = document ? findBlock(document.page, block.id) : undefined;
  const leadQuery = planned?.query ?? planned?.queries?.[0];
  const title = (spec && document ? renderedBlockTitle(spec, document, data, planned, leadQuery ? data?.queries[leadQuery] : undefined) : undefined) || block.title;
  const rows = (planned?.queries ?? (planned?.query ? [planned.query] : [])).reduce((total, id) => total + (data?.queries[id]?.rows.length ?? 0), 0);
  const sections = (
    <>
      {embedded ? about.map((text) => (
        <Typography color="text.secondary" key={text} sx={{ mb: 1.5, whiteSpace: 'pre-line' }} variant="body2">
          {text}
        </Typography>
      )) : null}

      <Section expanded={block.view === 'data'} id={embedded ? 'edit-block-data' : 'block-data'} summary={`${rows.toLocaleString('en-AU')} row${rows === 1 ? '' : 's'}`} title="Data">
        <Typography color="text.secondary" sx={{ fontSize: 12, mb: 1 }}>Rows returned for this chart, using the current dates and filters.</Typography>
        <BlockScanEstimate lensId={lensId} lensVersion={lensVersion} blockId={block.id} state={state} />
        <Box sx={{ mt: 1.5 }} />
        <BlockData block={block} data={data} onShowSql={block.view === 'sql' ? undefined : () => onView('sql')} onDownload={() => downloadLensCsv(lensId, { state, blocks: [block.id] }).catch(() => enqueueSnackbar(`${block.title} didn’t download. Try again.`, { variant: 'warning' }))} />
      </Section>
      <Section key={block.asked} expanded={block.view === 'sql'} id={embedded ? 'edit-block-about' : 'block-about'} title="Query details">
        <BlockQuery compact={!embedded} block={block} data={data} lensId={lensId} lensVersion={lensVersion} onShowSql={() => onView('sql')} sql={!embedded || block.view === 'sql'} state={state} />
      </Section>
    </>
  );
  if (!embedded) {
    const envelopes = (planned?.queries ?? (planned?.query ? [planned.query] : [])).map((id) => data?.queries[id]).filter((entry): entry is Envelope => Boolean(entry));
    const measures = [...new Map(envelopes.flatMap((envelope) => envelope.columns.filter((column) => column.role === 'measure').map((column) => [column.name, column] as const))).values()];
    const datasetIds = [...new Set(envelopes.flatMap((envelope) => envelope.columns.map((column) => column.dataset).filter(Boolean)))] as Array<string>;
    const datasetLabel = (id: string) => fields.data?.datasets.find((dataset) => dataset.id === id)?.label ?? id;
    const { reached, unreached } = document ? controlReach({ document, state }, envelopes) : { reached: [], unreached: [] };
    const filtered = reached.length || unreached.length
      ? `${reached.length ? `Filtered by ${reached.join(', ')}.` : ''}${reached.length && unreached.length ? ' ' : ''}${unreached.length ? `Not filtered by ${unreached.join(', ')}.` : ''}`
      : 'No filters are set.';
    return (
      <Box data-lens-block-pane={block.id} sx={{ px: 2, pb: 2, pt: 1.5 }}>
        <PanelHeader title={title}>
          {onEdit ? (
            <Button onClick={onEdit} size="small" startIcon={<TuneOutlinedIcon fontSize="small" />} sx={{ flexShrink: 0, textTransform: 'none' }} variant="text">
              Edit this block
            </Button>
          ) : null}
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </PanelHeader>
        {block.view !== 'data' && <Stack data-lens-block-about spacing={1.5}>
          {periodLine(data) ? (
            <Typography color="text.secondary" variant="body2">
              {periodLine(data)}
            </Typography>
          ) : null}
          {about.map((text) => (
            <Typography key={text} sx={{ whiteSpace: 'pre-line' }} variant="body2">
              {text}
            </Typography>
          ))}
          <Heading>What it measures</Heading>
          {measures.length ? (
            measures.map((column) => (
              <Box key={column.name}>
                <Stack alignItems="center" direction="row" spacing={0.75}>
                  <Typography sx={{ fontWeight: 600 }} variant="body2">
                    {column.label}
                  </Typography>
                  {column.certified ? <Pill>Certified</Pill> : null}
                </Stack>
                <Typography color="text.secondary" variant="body2">
                  {column.definition}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                  Field: {column.name}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography color="text.secondary" variant="body2">
              This block shows no measures.
            </Typography>
          )}
          <Heading>Where it comes from</Heading>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            {datasetIds.length ? datasetIds.map((id) => <Pill key={id}>{datasetLabel(id)}</Pill>) : <Typography color="text.secondary" variant="body2">No dataset is recorded for this block.</Typography>}
          </Stack>
          <Heading>Filters</Heading>
          <Typography variant="body2">{filtered}</Typography>

        </Stack>}
        <Box sx={{ mt: 1.5 }}>{sections}</Box>
      </Box>
    );
  }
  if (embedded)
    return (
      <Box data-lens-block-pane={block.id} sx={{ minWidth: 0 }}>
        {sections}
      </Box>
    );
  return (
    <Box data-lens-block-pane={block.id} sx={{ px: 2, pb: 2, pt: 1.5 }}>
      <PanelHeader title={title}>
        {onEdit ? (
          <Button onClick={onEdit} size="small" startIcon={<TuneOutlinedIcon fontSize="small" />} sx={{ flexShrink: 0, textTransform: 'none' }} variant="text">
            Edit this block
          </Button>
        ) : null}
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </PanelHeader>
      {sections}
    </Box>
  );
}
